import { LogService, MySquid, onConfigChange, onProfileCompletenessChange, onUserChange, ProfileCompleteness, User } from '@hub/shared'
import ItemRegister from '@src/components/register-completeness/components/item-register/item-register.component'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import './register-completeness.component.scss'

interface ItemCompletedProfile {
  value: string
  description: string
  completed: boolean
  link: string | null
}

interface RegisterCompletenessProps {
  onVisibilityChange: (isVisible: boolean) => void
}

const RegisterCompletenessComponent = ({ onVisibilityChange }: RegisterCompletenessProps) => {
  const [t] = useTranslation('registerComponent')
  const logService = useMemo(() => new LogService(), [])
  const [configs, setConfig] = useState<MySquid | null>(null)
  const [profileCompleted, setProfileCompleted] = useState<ProfileCompleteness | null>(null)
  const onConfigChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onProfileChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [allCompleteness, setAllCompleteness] = useState<boolean | undefined>(false)
  logService.init('Register completeness')
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [user, setUser] = useState<User | null>(null)

  const profileCompletedItems = [
    {
      value: 'register',
      description: 'register',
      completed: true,
      link: null,
    },
    {
      value: 'profile',
      description: 'completeProfile',
      completed: false,
      link: '/profile/data',
    },
    {
      value: 'phone',
      description: 'completePhone',
      completed: false,
      link: '/profile/data',
    },
    {
      value: 'address',
      description: 'completeAddress',
      completed: false,
      link: '/profile/address',
    },
    {
      value: 'socialNetwork',
      description: 'connectSocial',
      completed: false,
      link: '/profile/social-networks',
    },
  ]

  const [itemsCompletedProfile, setItemsCompletedProfile] = useState<ItemCompletedProfile[] | undefined>(profileCompletedItems)

  const handleCheckCompletenness = () => {
    if (profileCompleted) {
      const initialProfileItems = configs?._id ? itemsCompletedProfile?.filter((e) => e?.value !== 'phone') : itemsCompletedProfile
      const nextItemsCompletedProfile = initialProfileItems?.flatMap((itemInitial) => {
        if (itemInitial?.value === 'profile') {
          const fieldsCompleted =
            profileCompleted?.registration?.detail?.birthday &&
            profileCompleted?.registration?.detail?.document &&
            profileCompleted?.registration?.detail?.documentType &&
            profileCompleted?.registration?.detail?.email &&
            profileCompleted?.registration?.detail?.gender &&
            profileCompleted?.registration?.detail?.name &&
            profileCompleted?.registration?.detail?.nationality &&
            profileCompleted?.registration?.detail?.race &&
            profileCompleted?.registration?.detail?.phone
          return {
            ...itemInitial,
            completed: fieldsCompleted,
          }
        }
        if (itemInitial?.value === 'phone') {
          return {
            ...itemInitial,
            completed: profileCompleted?.registration?.detail?.phoneValid,
          }
        }
        if (itemInitial?.value === 'address') {
          const addressCompleted =
            profileCompleted?.registration?.detail?.country &&
            profileCompleted?.registration?.detail?.zipcode &&
            profileCompleted?.registration?.detail?.address &&
            profileCompleted?.registration?.detail?.city &&
            profileCompleted?.registration?.detail?.uf
          return {
            ...itemInitial,
            completed: addressCompleted,
          }
        }
        if (itemInitial?.value === 'socialNetwork') {
          const socialNetworkCompleted = !!user?.identities?.length
          return {
            ...itemInitial,
            completed: socialNetworkCompleted,
          }
        }
        return itemInitial
      })
      setItemsCompletedProfile(nextItemsCompletedProfile)
      const completed = nextItemsCompletedProfile?.every((element) => element?.completed === true)
      setAllCompleteness(completed)
    }
  }

  useEffect(() => {
    onConfigChangeSubscription.current = onConfigChange.subscribe((configChanged?: MySquid | any) => {
      if (configChanged?._id) {
        setConfig(configChanged)
      }
    })

    onProfileChangeSubscription.current = onProfileCompletenessChange.subscribe((profileCompleteness) => {
      if (profileCompleteness) {
        setProfileCompleted(profileCompleteness)
      }
    })

    onUserChangeSubscription.current = onUserChange.subscribe((userChanged: User) => {
      if (userChanged?.squidId) {
        setUser(userChanged)
      }
    })

    return () => {
      onConfigChangeSubscription?.current?.unsubscribe()
      onProfileChangeSubscription?.current?.unsubscribe()
      onUserChangeSubscription?.current?.unsubscribe()
    }
  }, [])

  useEffect(() => {
    handleCheckCompletenness()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileCompleted, t])

  const isVisible = !allCompleteness

  useEffect(() => {
    onVisibilityChange(isVisible)
  }, [isVisible, onVisibilityChange])

  if (!isVisible) return null

  if (allCompleteness) {
    return null
  }

  return (
    <div className="mr-2 box-register">
      <span dangerouslySetInnerHTML={{ __html: t('title') }}></span>
      <div className="mt-4">
        {itemsCompletedProfile?.map((item, index) => (
          <ItemRegister
            key={`${item?.value}-${index}`}
            description={t(item?.description)}
            completed={item?.completed}
            position={index + 1}
            link={item?.link}
            className="my-4"
          ></ItemRegister>
        ))}
      </div>
    </div>
  )
}

export default RegisterCompletenessComponent
